// features/buyer/cart/CartPage.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../../../contexts/CartContext';
import { useAuth } from '../../../contexts/AuthContext';
import { db } from '../../../firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import {
  ShoppingCart,
  Trash2,
  Plus,
  Minus,
  ArrowLeft,
  IndianRupee,
  Package,
  Truck,
  Shield,
  AlertCircle,
  ChevronRight,
  ShoppingBag,
  X,
  Percent,
  CreditCard,
  ShieldCheck,
  Clock,
  Award,
  Gauge,
  Info
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const CartPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { cartItems, cartCount, cartTotal, updateQuantity, removeFromCart, clearCart } = useCart();
  const [processing, setProcessing] = useState(false);
  const [productDetails, setProductDetails] = useState({});
  const [loadingDetails, setLoadingDetails] = useState(true);

  // Fee constants
  const RAZORPAY_FEE_PERCENTAGE = 2; // 2% processing fee
  const GST_ON_FEE_PERCENTAGE = 18; // 18% GST on processing fee

  // Format warranty for display
  const formatWarranty = (warranty) => {
    if (!warranty || warranty === 'No Warranty') {
      return {
        text: 'No Warranty',
        icon: Shield,
        color: 'text-gray-600',
        bgColor: 'bg-gray-100',
        borderColor: 'border-gray-200'
      };
    }
    
    if (warranty.includes('Lifetime')) {
      return {
        text: 'Lifetime Warranty',
        icon: Award,
        color: 'text-purple-600',
        bgColor: 'bg-purple-100',
        borderColor: 'border-purple-200'
      };
    }
    
    if (warranty.includes('Manufacturer')) {
      return {
        text: warranty,
        icon: ShieldCheck,
        color: 'text-blue-600',
        bgColor: 'bg-blue-100',
        borderColor: 'border-blue-200'
      };
    }
    
    if (warranty.includes('Seller')) {
      return {
        text: warranty,
        icon: Shield,
        color: 'text-green-600',
        bgColor: 'bg-green-100',
        borderColor: 'border-green-200'
      };
    }
    
    return {
      text: warranty,
      icon: Clock,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
      borderColor: 'border-orange-200'
    };
  };

  // Fetch product details (GST, MOQ, Warranty) for all products in cart
  useEffect(() => {
    const fetchProductDetails = async () => {
      if (cartItems.length === 0) {
        setLoadingDetails(false);
        return;
      }

      setLoadingDetails(true);
      const details = {};

      try {
        // Fetch details for each unique product
        for (const item of cartItems) {
          if (!details[item.productId]) {
            const productRef = doc(db, 'products', item.productId);
            const productSnap = await getDoc(productRef);
            
            if (productSnap.exists()) {
              const productData = productSnap.data();
              
              // Get GST rate (default to 18%)
              const gstRate = productData.gst || '18%';
              const gstNumeric = parseFloat(gstRate.replace('%', '')) || 18;
              
              // Get MOQ (default to 1)
              const moq = productData.moq || 1;
              
              // Get Warranty (default to 'No Warranty')
              const warranty = productData.warranty || 'No Warranty';
              
              details[item.productId] = {
                gst: {
                  rate: gstRate,
                  numeric: gstNumeric
                },
                moq: moq,
                warranty: warranty,
                unit: productData.unit || 'PCS',
                stock: productData.stock || productData.quantity || Infinity
              };
            } else {
              // Default if product not found
              details[item.productId] = {
                gst: { rate: '18%', numeric: 18 },
                moq: 1,
                warranty: 'No Warranty',
                unit: 'PCS',
                stock: Infinity
              };
            }
          }
        }
        
        setProductDetails(details);
        console.log('Fetched product details:', details);
        
        // Validate cart quantities against MOQ
        cartItems.forEach(item => {
          const productDetail = details[item.productId];
          if (productDetail && item.quantity < productDetail.moq) {
            toast.error(
              `${item.productName} quantity (${item.quantity}) is below minimum order quantity (${productDetail.moq} ${productDetail.unit}). Updating to MOQ.`
            );
            updateQuantity(item.productId, item.selectedColor || '', productDetail.moq);
          }
        });
        
      } catch (error) {
        console.error('Error fetching product details:', error);
        toast.error('Failed to load product information');
        
        // Set default details as fallback
        const defaultDetails = {};
        cartItems.forEach(item => {
          defaultDetails[item.productId] = {
            gst: { rate: '18%', numeric: 18 },
            moq: 1,
            warranty: 'No Warranty',
            unit: 'PCS',
            stock: Infinity
          };
        });
        setProductDetails(defaultDetails);
      } finally {
        setLoadingDetails(false);
      }
    };

    fetchProductDetails();
  }, [cartItems, updateQuantity]);

  // Calculate subtotal for a specific item
  const calculateItemSubtotal = (item) => {
    return item.unitPrice * item.quantity;
  };

  // Calculate GST amount for a specific item
  const calculateItemGst = (item) => {
    const gstInfo = productDetails[item.productId]?.gst || { numeric: 18 };
    const itemSubtotal = calculateItemSubtotal(item);
    return (itemSubtotal * gstInfo.numeric) / 100;
  };

  // Calculate total GST across all items
  const calculateTotalGst = () => {
    let totalGst = 0;
    cartItems.forEach(item => {
      totalGst += calculateItemGst(item);
    });
    return totalGst;
  };

  // Calculate subtotal with product GST (amount before payment fees)
  const calculateSubtotalWithGst = () => {
    return cartTotal + calculateTotalGst();
  };

  // Calculate Razorpay processing fee (2% of subtotal including GST)
  const calculateRazorpayFee = () => {
    const subtotalWithGst = calculateSubtotalWithGst();
    return (subtotalWithGst * RAZORPAY_FEE_PERCENTAGE) / 100;
  };

  // Calculate GST on Razorpay fee (18% of processing fee)
  const calculateGstOnFee = () => {
    const razorpayFee = calculateRazorpayFee();
    return (razorpayFee * GST_ON_FEE_PERCENTAGE) / 100;
  };

  // Calculate total payable amount (subtotal + GST + processing fee + GST on fee)
  const calculateTotalPayable = () => {
    const subtotalWithGst = calculateSubtotalWithGst();
    const razorpayFee = calculateRazorpayFee();
    const gstOnFee = calculateGstOnFee();
    return subtotalWithGst + razorpayFee + gstOnFee;
  };

  // Get GST display rate for an item
  const getItemGstRate = (item) => {
    return productDetails[item.productId]?.gst?.rate || '18%';
  };

  // Get MOQ for an item
  const getItemMoq = (item) => {
    return productDetails[item.productId]?.moq || 1;
  };

  // Get Warranty info for an item
  const getItemWarrantyInfo = (item) => {
    const warranty = productDetails[item.productId]?.warranty || 'No Warranty';
    return formatWarranty(warranty);
  };

  // Get Unit for an item
  const getItemUnit = (item) => {
    return productDetails[item.productId]?.unit || 'PCS';
  };

  // Handle quantity change with MOQ validation
  const handleQuantityChange = (item, newQuantity) => {
    const moq = getItemMoq(item);
    const unit = getItemUnit(item);
    const maxQuantity = item.maxQuantity || productDetails[item.productId]?.stock || Infinity;
    
    // Validate against MOQ
    if (newQuantity < moq) {
      toast.error(`Minimum order quantity is ${moq} ${unit}`);
      return;
    }
    
    // Validate against stock
    if (newQuantity > maxQuantity) {
      toast.error(`Only ${maxQuantity} ${unit} available`);
      return;
    }
    
    // Validate that quantity is multiple of MOQ (optional but recommended)
    if (newQuantity % moq !== 0) {
      toast.error(`Quantity must be in multiples of ${moq} ${unit}`);
      return;
    }
    
    updateQuantity(item.productId, item.selectedColor || '', newQuantity);
  };

  // Handle remove item
  const handleRemoveItem = (item) => {
    if (window.confirm('Remove this item from cart?')) {
      removeFromCart(item.productId, item.selectedColor || '');
    }
  };

  // Handle proceed to checkout
  const handleCheckout = () => {
    if (!user) {
      toast.error('Please login to checkout');
      navigate('/login');
      return;
    }

    if (cartItems.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    // Validate all items against MOQ before checkout
    let hasError = false;
    cartItems.forEach(item => {
      const moq = getItemMoq(item);
      const unit = getItemUnit(item);
      
      if (item.quantity < moq) {
        toast.error(
          `${item.productName} quantity (${item.quantity}) is below minimum order quantity (${moq} ${unit})`
        );
        hasError = true;
      }
      
      if (item.quantity % moq !== 0) {
        toast.error(
          `${item.productName} quantity must be in multiples of ${moq} ${unit}`
        );
        hasError = true;
      }
    });

    if (hasError) return;

    setProcessing(true);
    // Navigate to checkout page
    setTimeout(() => {
      navigate('/checkout');
    }, 500);
  };

  // Format currency
  const formatINR = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount).replace('₹', '');
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <ShoppingCart className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Please Login to View Cart</h2>
            <p className="text-gray-600 mb-6">You need to be logged in to view your shopping cart</p>
            <Link
              to="/login"
              className="inline-flex items-center px-6 py-3 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-colors"
            >
              Login to Continue
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="flex items-center text-sm text-gray-600 mb-6">
          <Link to="/" className="hover:text-orange-600">Home</Link>
          <ChevronRight className="h-4 w-4 mx-2" />
          <span className="text-gray-900 font-medium">Shopping Cart</span>
        </nav>

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 flex items-center">
            <ShoppingCart className="h-8 w-8 mr-3 text-orange-600" />
            Shopping Cart ({cartCount} {cartCount === 1 ? 'item' : 'items'})
          </h1>
          
          {cartItems.length > 0 && (
            <button
              onClick={clearCart}
              className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center text-sm font-medium"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Clear Cart
            </button>
          )}
        </div>

        {cartItems.length === 0 ? (
          // Empty Cart
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <div className="w-24 h-24 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <ShoppingBag className="h-12 w-12 text-orange-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Your cart is empty</h2>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              Looks like you haven't added any items to your cart yet. Start shopping to add items.
            </p>
            <Link
              to="/buyer/products"
              className="inline-flex items-center px-6 py-3 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-colors"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Continue Shopping
            </Link>
          </div>
        ) : (
          // Cart with items
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {cartItems.map((item, index) => {
                const itemSubtotal = calculateItemSubtotal(item);
                const itemGst = calculateItemGst(item);
                const itemTotal = itemSubtotal + itemGst;
                const gstRate = getItemGstRate(item);
                const moq = getItemMoq(item);
                const unit = getItemUnit(item);
                const warrantyInfo = getItemWarrantyInfo(item);
                const WarrantyIcon = warrantyInfo.icon;
                
                return (
                  <div
                    key={`${item.productId}-${item.selectedColor || ''}-${index}`}
                    className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
                  >
                    <div className="p-6">
                      <div className="flex flex-col sm:flex-row gap-4">
                        {/* Product Image */}
                        <Link to={`/buyer/products/${item.productId}`} className="sm:w-24 sm:h-24 flex-shrink-0">
                          <div className="w-full h-24 sm:h-24 bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                            <img
                              src={item.productImage || '/placeholder-image.jpg'}
                              alt={item.productName}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        </Link>

                        {/* Product Details */}
                        <div className="flex-1">
                          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                            <div>
                              <Link
                                to={`/buyer/products/${item.productId}`}
                                className="text-lg font-semibold text-gray-900 hover:text-orange-600 transition-colors"
                              >
                                {item.productName}
                              </Link>
                              
                              {/* Color if selected */}
                              {item.selectedColor && (
                                <div className="flex items-center mt-1 text-sm">
                                  <span className="text-gray-600">Color:</span>
                                  <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                                    {item.selectedColor}
                                  </span>
                                </div>
                              )}

                              {/* Warranty Badge */}
                              {!loadingDetails && item.warranty && item.warranty !== 'No Warranty' && (
                                <div className={`inline-flex items-center text-xs ${warrantyInfo.color} ${warrantyInfo.bgColor} px-2 py-1 rounded-md border ${warrantyInfo.borderColor} mt-1`}>
                                  <WarrantyIcon className="w-3 h-3 mr-1" />
                                  {warrantyInfo.text}
                                </div>
                              )}

                              {/* MOQ Badge */}
                              {!loadingDetails && (
                                <div className="flex items-center mt-1 text-xs text-gray-600">
                                  <Gauge className="w-3 h-3 mr-1 text-gray-400" />
                                  <span>MOQ: {moq} {unit}</span>
                                </div>
                              )}

                              {/* GST Rate Badge */}
                              {!loadingDetails && (
                                <div className="flex items-center mt-1 text-xs text-gray-600">
                                  <Percent className="w-3 h-3 mr-1 text-gray-400" />
                                  <span>GST: {gstRate}</span>
                                </div>
                              )}

                              {/* Seller */}
                              <p className="text-sm text-gray-600 mt-1">
                                Seller: {item.sellerName || 'Unknown'}
                              </p>
                            </div>

                            {/* Price Breakdown */}
                            <div className="text-right">
                              <div className="text-xl font-bold text-orange-600 flex items-center justify-end">
                                <IndianRupee className="h-4 w-4" />
                                {formatINR(itemTotal)}
                              </div>
                              <p className="text-xs text-gray-500">
                                Base: ₹{formatINR(itemSubtotal)} + GST: ₹{formatINR(itemGst)}
                              </p>
                              <p className="text-xs text-gray-400">
                                ₹{formatINR(item.unitPrice)} × {item.quantity}
                              </p>
                            </div>
                          </div>

                          {/* Quantity Controls with MOQ Info */}
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between mt-4 pt-4 border-t border-gray-100">
                            <div className="flex items-center space-x-3">
                              <span className="text-sm text-gray-600">Qty:</span>
                              <div className="flex items-center border border-gray-300 rounded-lg">
                                <button
                                  onClick={() => handleQuantityChange(item, item.quantity - moq)}
                                  className="p-2 hover:bg-gray-50 disabled:opacity-50"
                                  disabled={item.quantity <= moq}
                                  title={`Decrease by MOQ (${moq})`}
                                >
                                  <Minus className="h-3 w-3" />
                                </button>
                                <span className="w-12 text-center font-medium">
                                  {item.quantity}
                                </span>
                                <button
                                  onClick={() => handleQuantityChange(item, item.quantity + moq)}
                                  className="p-2 hover:bg-gray-50 disabled:opacity-50"
                                  disabled={item.quantity >= (item.maxQuantity || Infinity)}
                                  title={`Increase by MOQ (${moq})`}
                                >
                                  <Plus className="h-3 w-3" />
                                </button>
                              </div>
                              <span className="text-xs text-gray-500 flex items-center">
                                <Info className="h-3 w-3 mr-1 text-orange-400" />
                                MOQ: {moq} {unit}
                              </span>
                              <span className="text-xs text-gray-500">
                                Max: {item.maxQuantity || 'N/A'}
                              </span>
                            </div>

                            <div className="flex items-center space-x-4 mt-2 sm:mt-0">
                              <button
                                onClick={() => handleRemoveItem(item)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-colors flex items-center text-sm"
                              >
                                <Trash2 className="h-4 w-4 mr-1" />
                                Remove
                              </button>
                            </div>
                          </div>

                          {/* Warranty Disclaimer */}
                          {item.warranty && item.warranty !== 'No Warranty' && (
                            <div className="mt-2 text-xs text-gray-500 bg-gray-50 p-2 rounded-lg">
                              <span className="font-medium">Warranty:</span> {item.warranty} applies to this item
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-lg p-6 sticky top-24">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Payment Summary</h2>
                
                {loadingDetails ? (
                  <div className="space-y-3 mb-6">
                    <div className="animate-pulse">
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="space-y-3 mb-6">
                      {/* Product Subtotal */}
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Subtotal ({cartCount} items)</span>
                        <span className="font-medium flex items-center">
                          <IndianRupee className="h-3 w-3" />
                          {formatINR(cartTotal)}
                        </span>
                      </div>
                      
                      {/* Product GST Breakdown by rate */}
                      {Object.entries(
                        cartItems.reduce((acc, item) => {
                          const rate = getItemGstRate(item);
                          if (!acc[rate]) acc[rate] = 0;
                          acc[rate] += calculateItemGst(item);
                          return acc;
                        }, {})
                      ).map(([rate, amount]) => (
                        <div key={rate} className="flex justify-between text-sm">
                          <span className="text-gray-600">GST on products ({rate})</span>
                          <span className="font-medium flex items-center">
                            <IndianRupee className="h-3 w-3" />
                            {formatINR(amount)}
                          </span>
                        </div>
                      ))}
                      
                      {/* Subtotal with Product GST */}
                      <div className="flex justify-between text-sm font-medium pt-2 border-t border-gray-100">
                        <span className="text-gray-800">Subtotal (incl. product GST)</span>
                        <span className="text-gray-900 flex items-center">
                          <IndianRupee className="h-3 w-3" />
                          {formatINR(calculateSubtotalWithGst())}
                        </span>
                      </div>

                      {/* Payment Gateway Fees */}
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                          <CreditCard className="h-4 w-4 mr-1 text-orange-600" />
                          Payment Gateway Charges
                        </h3>
                        
                        {/* Razorpay Processing Fee */}
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">
                            Razorpay processing fee (2%)
                          </span>
                          <span className="font-medium flex items-center">
                            <IndianRupee className="h-3 w-3" />
                            {formatINR(calculateRazorpayFee())}
                          </span>
                        </div>
                        
                        {/* GST on Processing Fee */}
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">
                            GST on processing fee (18%)
                          </span>
                          <span className="font-medium flex items-center">
                            <IndianRupee className="h-3 w-3" />
                            {formatINR(calculateGstOnFee())}
                          </span>
                        </div>
                        
                        {/* Example calculation note */}
                        <div className="mt-2 p-2 bg-gray-50 rounded-lg text-xs text-gray-600">
                          <p className="font-medium mb-1">Example calculation:</p>
                          <p>On ₹1000, fee = ₹20 + GST (18%) = ₹3.60</p>
                        </div>
                      </div>
                      
                      {/* Shipping */}
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Shipping</span>
                        <span className="font-medium text-green-600">Extra</span>
                      </div>
                      
                      {/* Total Payable Amount */}
                      <div className="border-t-2 border-orange-200 pt-3 mt-3">
                        <div className="flex justify-between font-bold">
                          <span className="text-gray-900">Total Payable Amount</span>
                          <span className="text-xl text-orange-600 flex items-center">
                            <IndianRupee className="h-4 w-4" />
                            {formatINR(calculateTotalPayable())}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Includes all taxes and payment gateway charges
                        </p>
                      </div>

                      {/* MOQ Summary */}
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                          <Gauge className="h-4 w-4 mr-1 text-orange-600" />
                          Minimum Order Quantities
                        </h3>
                        {cartItems.map((item, idx) => {
                          const moq = getItemMoq(item);
                          const unit = getItemUnit(item);
                          return (
                            <div key={idx} className="flex justify-between text-xs text-gray-600 mb-1">
                              <span className="truncate max-w-[150px]">{item.productName}:</span>
                              <span className="font-medium">{moq} {unit}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Checkout Button */}
                    <button
                      onClick={handleCheckout}
                      disabled={processing || cartItems.length === 0}
                      className={`w-full py-3 rounded-lg font-medium transition-all transform hover:scale-105 mb-4 ${
                        processing
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-orange-600 text-white hover:bg-orange-700'
                      }`}
                    >
                      {processing ? 'Processing...' : 'Proceed to Checkout'}
                    </button>

                    {/* Continue Shopping */}
                    <Link
                      to="/buyer/products"
                      className="w-full py-2 text-center text-orange-600 hover:text-orange-700 font-medium flex items-center justify-center"
                    >
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Continue Shopping
                    </Link>
                  </>
                )}

                {/* Features */}
                <div className="mt-6 space-y-3 pt-4 border-t border-gray-200">
                  {/* <div className="flex items-center text-sm text-gray-600">
                    <Truck className="h-4 w-4 mr-2 text-orange-600" />
                    Free shipping on orders above ₹5000
                  </div> */}
                  <div className="flex items-center text-sm text-gray-600">
                    <Shield className="h-4 w-4 mr-2 text-orange-600" />
                    Secure payment via Razorpay
                  </div>
                  {/* <div className="flex items-center text-sm text-gray-600">
                    <Package className="h-4 w-4 mr-2 text-orange-600" />
                    7 days easy returns
                  </div> */}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartPage;